import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { Check, ChevronDown, Users } from 'lucide-react';
import { IconArrowsDownUp, IconCalendarCog, IconColumns3, IconDeviceMobileSearch } from '@tabler/icons-react';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
  useEcFilterPopoverLayout,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import { Ci2HelpHexIcon } from '../../process-config/components/Ci2HelpHexIcon';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import './GoUsuariosConfig.css';
import { GoHoverTooltip } from './GoHoverTooltip';
import { GoUsrCodePills, GoUsrCodePicker } from './GoUsrCodePills';
import {
  buildCodesPatchForTipo,
  buildGoCodeUsage,
  codesPolicyForTipo,
  goCodeConflictMessage,
  isGoCodeTaken,
  sanitizeGoCode,
} from '../utils/goUserCodeRules';
import { useUserProfilePhotos } from '../../user-management/hooks/useUserProfilePhotos';

const API_BASE_URL = getApiBaseUrl();

const TIPO_USER_OPTIONS = [
  {
    value: 1,
    label: 'Técnico',
    desc: 'Atendimento no App Go',
    color: 'var(--ci2-violet, #6E56CF)',
  },
  {
    value: 2,
    label: 'Entregador',
    desc: 'Entrega no App Go',
    color: 'var(--ci2-blue, #265ED4)',
  },
  {
    value: 3,
    label: 'Ambos',
    desc: 'Atendimento e entrega',
    color: 'var(--ci2-teal, #0E8A82)',
  },
];

function GoUsrRequiredCodeFields({
  tipoUser,
  codtech,
  onCodtechChange,
  codmoto,
  onCodmotoChange,
  disabled,
  idPrefix = 'go-usr-code',
  catalog,
  usedTech,
  usedMoto,
  excludeIdUser,
}) {
  const codePolicy = codesPolicyForTipo(tipoUser);
  if (!codePolicy.needCodtech && !codePolicy.needCodmoto) return null;

  return (
    <div className="go-usr-enable-codes">
      {codePolicy.needCodtech ? (
        <div className="go-usr-enable-code-field">
          <label className="go-usr-enable-pwd-label" htmlFor={`${idPrefix}-codtech`}>
            Código do técnico <span className="go-usr-req">*</span>
          </label>
          <GoUsrCodePicker
            field="codtech"
            value={codtech}
            onChange={onCodtechChange}
            catalog={catalog}
            usedMap={usedTech}
            excludeIdUser={excludeIdUser}
            disabled={disabled}
            inputId={`${idPrefix}-codtech`}
          />
        </div>
      ) : null}
      {codePolicy.needCodmoto ? (
        <div className="go-usr-enable-code-field">
          <label className="go-usr-enable-pwd-label" htmlFor={`${idPrefix}-codmoto`}>
            Código do motorista <span className="go-usr-req">*</span>
          </label>
          <GoUsrCodePicker
            field="codmoto"
            value={codmoto}
            onChange={onCodmotoChange}
            catalog={catalog}
            usedMap={usedMoto}
            excludeIdUser={excludeIdUser}
            disabled={disabled}
            inputId={`${idPrefix}-codmoto`}
          />
        </div>
      ) : null}
    </div>
  );
}

function GoEnableTipoModal({
  tipoUser,
  onTipoChange,
  enableCodtech,
  onEnableCodtechChange,
  enableCodmoto,
  onEnableCodmotoChange,
  onConfirm,
  onCancel,
  submitting,
  catalog,
  usedTech,
  usedMoto,
  excludeIdUser,
}) {
  const builtCodes = buildCodesPatchForTipo(tipoUser, {
    codtechInput: enableCodtech,
    codmotoInput: enableCodmoto,
  });
  const codesOk =
    builtCodes.ok &&
    !isGoCodeTaken(usedTech, enableCodtech, excludeIdUser) &&
    !isGoCodeTaken(usedMoto, enableCodmoto, excludeIdUser);

  return createPortal(
    <div className="go-usr-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="go-usr-enable-tipo-title">
      <div className="go-usr-confirm-modal">
        <h3 id="go-usr-enable-tipo-title" className="go-usr-confirm-title">
          Tipo de usuário
        </h3>
        <p className="go-usr-confirm-message">
          Defina se este usuário atuará como técnico, entregador ou ambos no App Go.
        </p>
        <div className="go-usr-tipo-options" role="radiogroup" aria-label="Tipo de usuário">
          {TIPO_USER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={tipoUser === opt.value}
              className={`go-usr-tipo-option${tipoUser === opt.value ? ' selected' : ''}`}
              onClick={() => onTipoChange(opt.value)}
              disabled={submitting}
            >
              <span className="go-usr-tipo-option-dot" style={{ background: opt.color }} aria-hidden />
              <span className="go-usr-tipo-option-text">
                <span className="go-usr-tipo-option-label">{opt.label}</span>
                <span className="go-usr-tipo-option-desc">{opt.desc}</span>
              </span>
            </button>
          ))}
        </div>

        <GoUsrRequiredCodeFields
          tipoUser={tipoUser}
          codtech={enableCodtech}
          onCodtechChange={onEnableCodtechChange}
          codmoto={enableCodmoto}
          onCodmotoChange={onEnableCodmotoChange}
          disabled={submitting}
          idPrefix="go-usr-enable"
          catalog={catalog}
          usedTech={usedTech}
          usedMoto={usedMoto}
          excludeIdUser={excludeIdUser}
        />

        <div className="go-usr-confirm-actions">
          <button
            type="button"
            className="user-form-link-btn user-form-footer-btn"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={onConfirm}
            disabled={submitting || !tipoUser || !codesOk}
          >
            {submitting ? 'Habilitando...' : 'Habilitar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function GoTipoChangeCodesModal({
  username,
  tipoUser,
  codtech,
  onCodtechChange,
  codmoto,
  onCodmotoChange,
  onConfirm,
  onCancel,
  submitting,
  catalog,
  usedTech,
  usedMoto,
  excludeIdUser,
}) {
  const tipoLabel = TIPO_USER_OPTIONS.find((o) => o.value === Number(tipoUser))?.label || 'selecionado';
  const builtCodes = buildCodesPatchForTipo(tipoUser, {
    codtechInput: codtech,
    codmotoInput: codmoto,
  });
  const codesOk =
    builtCodes.ok &&
    !isGoCodeTaken(usedTech, codtech, excludeIdUser) &&
    !isGoCodeTaken(usedMoto, codmoto, excludeIdUser);

  return createPortal(
    <div className="go-usr-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="go-usr-tipo-codes-title">
      <div className="go-usr-confirm-modal">
        <h3 id="go-usr-tipo-codes-title" className="go-usr-confirm-title">
          Códigos obrigatórios
        </h3>
        <p className="go-usr-confirm-message">
          Para alterar <strong>{username || 'este usuário'}</strong> para <strong>{tipoLabel}</strong>,
          informe os códigos exigidos (4 dígitos). Campos incompatíveis serão limpos.
        </p>
        <GoUsrRequiredCodeFields
          tipoUser={tipoUser}
          codtech={codtech}
          onCodtechChange={onCodtechChange}
          codmoto={codmoto}
          onCodmotoChange={onCodmotoChange}
          disabled={submitting}
          idPrefix="go-usr-tipo-change"
          catalog={catalog}
          usedTech={usedTech}
          usedMoto={usedMoto}
          excludeIdUser={excludeIdUser}
        />
        <div className="go-usr-confirm-actions">
          <button
            type="button"
            className="user-form-link-btn user-form-footer-btn"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={onConfirm}
            disabled={submitting || !codesOk}
          >
            {submitting ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function GoDisableConfirmModal({ username, onConfirm, onCancel, submitting }) {
  return createPortal(
    <div className="go-usr-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="go-usr-disable-title">
      <div className="go-usr-confirm-modal">
        <h3 id="go-usr-disable-title" className="go-usr-confirm-title">
          Desabilitar no App Go
        </h3>
        <p className="go-usr-confirm-message">
          Remover <strong>{username || 'este usuário'}</strong> do App Go? O vínculo mobile será
          excluído e o usuário voltará para o grupo Não habilitados.
        </p>
        <div className="go-usr-confirm-actions">
          <button
            type="button"
            className="user-form-link-btn user-form-footer-btn"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? 'Desabilitando...' : 'Desabilitar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const TOOLTIP_TRANSPORTE =
  'Marcar esta opção desativa o campo de quilometragem nos atendimentos. O usuário não precisará (e não poderá) informar a KM.';
const TOOLTIP_DESPESAS = 'Ao habilitar, o usuário tem acesso ao módulo controle de despesas';

const GO_GROUPS = [
  {
    key: 'tecnicos',
    label: 'Técnicos',
    desc: 'Atendimento no App Go',
    color: 'var(--ci2-violet, #6E56CF)',
  },
  {
    key: 'entregadores',
    label: 'Entregadores',
    desc: 'Entrega no App Go',
    color: 'var(--ci2-blue, #265ED4)',
  },
  {
    key: 'ambos',
    label: 'Ambos',
    desc: 'Atendimento e entrega',
    color: 'var(--ci2-teal, #0E8A82)',
  },
  {
    key: 'nao-habilitados',
    label: 'Não habilitados',
    desc: 'Técnicos ainda sem acesso ao App Go',
    color: 'var(--ci2-gray, #767889)',
  },
];

const EXPENSE_PERIOD_OPTIONS = [
  { value: 1, label: 'Semanal' },
  { value: 2, label: 'Quinzenal' },
  { value: 3, label: 'Mensal' },
];

const GO_COL_KEYS = ['tipo_user', 'use_public_transportation', 'asset_costs', 'allow_manual_wo', 'devices'];
const GO_COL_LABELS = {
  tipo_user: 'Tipo',
  use_public_transportation: 'Transporte público',
  asset_costs: 'Despesas',
  allow_manual_wo: 'Abre OS avulsa',
  devices: 'Dispositivo',
};
const GO_SORT_OPTIONS = [
  { key: 'username', label: 'Nome' },
  { key: 'codtec', label: 'Código' },
  ...GO_COL_KEYS.filter((k) => k !== 'devices').map((k) => ({ key: k, label: GO_COL_LABELS[k] })),
];

function formatUltimoAcesso(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function categorizeGoUser(row) {
  if (row.enabled === false) return 'nao-habilitados';
  if (row.tipoUser === 2) return 'entregadores';
  if (row.tipoUser === 3) return 'ambos';
  return 'tecnicos';
}

function fullColVisibility() {
  return GO_COL_KEYS.reduce((acc, k) => {
    acc[k] = true;
    return acc;
  }, {});
}

function GoThHelp({ text }) {
  return (
    <span className="pcs2-tooltip-host pcs2-info-tip-host go-usr-th-help" data-tooltip={text}>
      <Ci2HelpHexIcon label="Mais informações" />
    </span>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children, className = '' }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2Subblock({ title, desc, action, children }) {
  return (
    <div className="pcs2-subblock">
      {(title || desc || action) ? (
        <div className={`pcs2-subhead${action ? ' pcs2-subhead--with-action' : ''}`}>
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
          {action ? <div className="pcs2-subhead-action">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function GoToggle({ checked, onChange, disabled, ariaLabel, tooltip, tooltipAlign }) {
  const tip = tooltip || ariaLabel || '';
  const align = tooltipAlign === 'end' ? 'end' : tooltipAlign === 'start' ? 'start' : 'center';
  return (
    <GoHoverTooltip text={tip} align={align}>
      <button
        type="button"
        className={`go-usr-toggle${checked ? ' on' : ''}`}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
        aria-label={ariaLabel}
      >
        <span className="go-usr-toggle-knob" />
      </button>
    </GoHoverTooltip>
  );
}

function GoAvatar({ photoUrl, initials }) {
  if (photoUrl) {
    return <img src={photoUrl} alt="" className="go-usr-avatar-img" />;
  }
  return <div className="go-usr-avatar-fallback">{initials || '?'}</div>;
}

function ExpensePeriodPopover({ open, anchorRef, value, onSelect, onClose }) {
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null);
      return undefined;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 6,
      left: Math.min(rect.left, window.innerWidth - 180),
    });

    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={popRef}
      className="go-usr-expense-popover"
      style={{ top: pos.top, left: pos.left }}
      role="listbox"
      aria-label="Período de despesas"
    >
      {EXPENSE_PERIOD_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="option"
          aria-selected={value === opt.value}
          className={`go-usr-expense-option${value === opt.value ? ' selected' : ''}`}
          onClick={() => onSelect(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>,
    document.body
  );
}

function tipoUserLabel(tipoUser) {
  return TIPO_USER_OPTIONS.find((o) => o.value === Number(tipoUser))?.label || 'Técnico';
}

function TipoUserPopover({ open, anchorRef, value, onSelect, onClose }) {
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null);
      return undefined;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 6,
      left: Math.min(rect.left, window.innerWidth - 180),
    });

    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={popRef}
      className="go-usr-expense-popover"
      style={{ top: pos.top, left: pos.left }}
      role="listbox"
      aria-label="Tipo de usuário"
    >
      {TIPO_USER_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="option"
          aria-selected={value === opt.value}
          className={`go-usr-expense-option${value === opt.value ? ' selected' : ''}`}
          onClick={() => onSelect(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>,
    document.body
  );
}

function DevicePopover({ open, anchorRef, devices, onClose }) {
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null);
      return undefined;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const width = 220;
    const left = Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8);
    setPos({
      top: rect.bottom + 8,
      left,
      arrowLeft: rect.left + rect.width / 2 - left,
    });

    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={popRef}
      className="go-usr-device-popover"
      style={{ top: pos.top, left: pos.left }}
      role="dialog"
      aria-label="Dispositivos do usuário"
    >
      <span className="go-usr-device-popover-arrow" style={{ left: pos.arrowLeft }} aria-hidden />
      {(devices || []).length === 0 ? (
        <div className="go-usr-device-empty">Nenhum dispositivo registrado.</div>
      ) : (
        (devices || []).map((device, idx) => (
          <div key={`${device.modelo}-${idx}`} className="go-usr-device-item">
            <div className="go-usr-device-row">
              <span className="go-usr-device-label">Modelo</span>
              <span className="go-usr-device-value">{device.modelo || '—'}</span>
            </div>
            <div className="go-usr-device-row">
              <span className="go-usr-device-label">Último acesso</span>
              <span className="go-usr-device-value">{formatUltimoAcesso(device.ultimoAcesso)}</span>
            </div>
          </div>
        ))
      )}
    </div>,
    document.body
  );
}

function GoUsuariosToolbar({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  isColVis,
  onToggleCol,
}) {
  const sortBtnRef = useRef(null);
  const colsBtnRef = useRef(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [colsOpen, setColsOpen] = useState(false);

  const sortLayout = useEcFilterPopoverLayout(sortBtnRef, sortOpen, 198, 320);
  const colsLayout = useEcFilterPopoverLayout(colsBtnRef, colsOpen, 198, 320);

  const sortItems = useMemo(
    () => GO_SORT_OPTIONS.map((o) => ({ value: o.key, label: o.label })),
    []
  );
  const sortLabel = sortItems.find((i) => i.value === sortBy)?.label ?? 'Nome';

  const colItems = useMemo(
    () => GO_COL_KEYS.map((k) => ({ value: k, label: GO_COL_LABELS[k] })),
    []
  );
  const selectedColKeys = useMemo(
    () => colItems.filter((item) => isColVis(item.value)).map((item) => item.value),
    [colItems, isColVis]
  );
  const colsCustomized = selectedColKeys.length !== colItems.length;

  useEffect(() => {
    if (!sortOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-go-usr-sort-popover]');
      if (pop?.contains(e.target) || sortBtnRef.current?.contains(e.target)) return;
      setSortOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [sortOpen]);

  useEffect(() => {
    if (!colsOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-go-usr-cols-popover]');
      if (pop?.contains(e.target) || colsBtnRef.current?.contains(e.target)) return;
      setColsOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [colsOpen]);

  const handleColsToggleAll = useCallback(() => {
    const allMarked = colItems.every((item) => isColVis(item.value));
    colItems.forEach((item) => {
      const visible = isColVis(item.value);
      if (allMarked && visible) onToggleCol(item.value);
      if (!allMarked && !visible) onToggleCol(item.value);
    });
  }, [colItems, isColVis, onToggleCol]);

  const sortPopoverStyle = buildEcFilterPopoverStyle(sortLayout);
  const colsPopoverStyle = buildEcFilterPopoverStyle(colsLayout);

  return (
    <div className="go-usr-toolbar">
      <BoardToolbarExpandSearch
        value={searchQuery}
        onChange={onSearchChange}
        placeholder="Pesquisar usuários..."
        ariaLabel="Pesquisar usuários"
      />
      <EcClickUpFilterChip
        anchorRef={sortBtnRef}
        label={sortLabel}
        icon={IconArrowsDownUp}
        hasSelection={sortOpen}
        isOpen={sortOpen}
        showValue={false}
        ariaLabel="Ordenar tabela"
        onClick={() => {
          setColsOpen(false);
          setSortOpen((open) => !open);
        }}
      />
      {sortOpen && sortPopoverStyle
        ? ReactDOM.createPortal(
            <div
              data-go-usr-sort-popover
              className={EC_FILTER_POPOVER_CLASS}
              style={sortPopoverStyle}
              role="dialog"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <BoardSearchSimplePickerPanel
                showSearch={false}
                searchPlaceholder=""
                searchTerm=""
                onSearchChange={() => {}}
                items={sortItems}
                getKey={(item) => item.value}
                getLabel={(item) => item.label}
                selectedKeys={[sortBy]}
                onToggle={(item) => {
                  onSortChange(item.value);
                  setSortOpen(false);
                }}
                emptyMessage="Nenhuma coluna"
                listAriaLabel="Ordenar por"
              />
            </div>,
            document.body
          )
        : null}
      <EcClickUpFilterChip
        anchorRef={colsBtnRef}
        label="Colunas"
        icon={IconColumns3}
        hasSelection={colsCustomized}
        isOpen={colsOpen}
        showValue={false}
        ariaLabel="Personalizar colunas"
        onClick={() => {
          setSortOpen(false);
          setColsOpen((open) => !open);
        }}
      />
      {colsOpen && colsPopoverStyle
        ? ReactDOM.createPortal(
            <div
              data-go-usr-cols-popover
              className={EC_FILTER_POPOVER_CLASS}
              style={colsPopoverStyle}
              role="dialog"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <BoardSearchSimplePickerPanel
                showSearch={false}
                searchPlaceholder=""
                searchTerm=""
                onSearchChange={() => {}}
                items={colItems}
                getKey={(item) => item.value}
                getLabel={(item) => item.label}
                selectedKeys={selectedColKeys}
                onToggle={(item) => onToggleCol(item.value)}
                emptyMessage="Nenhuma coluna"
                listAriaLabel="Colunas visíveis"
                bulkMenuEnabled
                onBulkToggleAll={handleColsToggleAll}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

function GoEnableCell({
  row,
  busy,
  disabling,
  onEnable,
  onRequestDisable,
}) {
  if (row.enabled !== false) {
    return (
      <td className="go-usr-td go-usr-td-center go-usr-td-habilita">
        <div className="go-usr-permite-wrap">
          <GoHoverTooltip text="Clique para desabilitar no App Go" align="start">
            <button
              type="button"
              className="go-usr-lock-toggle on"
              onClick={(e) => {
                e.stopPropagation();
                onRequestDisable(row);
              }}
              disabled={busy || disabling}
              aria-pressed="true"
              aria-label={`Desabilitar ${row.username || 'usuário'}`}
            >
              <span className="go-usr-lock-knob">
                <Check size={7} strokeWidth={3} aria-hidden />
              </span>
            </button>
          </GoHoverTooltip>
        </div>
      </td>
    );
  }

  if (busy) {
    return (
      <td className="go-usr-td go-usr-td-center go-usr-td-habilita">
        <span className="go-usr-enabling">
          <span className="go-usr-enabling-spinner" aria-hidden />
        </span>
      </td>
    );
  }

  return (
    <td className="go-usr-td go-usr-td-center go-usr-td-habilita">
      <GoHoverTooltip text="Clique para habilitar no App Go" align="start">
        <button
          type="button"
          className="go-usr-enable-affordance"
          aria-label={`Habilitar ${row.username || 'usuário'}`}
          onClick={(e) => {
            e.stopPropagation();
            onEnable(row);
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </GoHoverTooltip>
    </td>
  );
}

function GoUserRow({
  row,
  photoUrl,
  hidden,
  savingKey,
  enablingId,
  disablingId,
  onPatch,
  onChangeTipoUser,
  onEnable,
  onRequestDisable,
  expenseOpenId,
  setExpenseOpenId,
  tipoOpenId,
  setTipoOpenId,
  deviceOpenId,
  setDeviceOpenId,
  codeEdit,
  onOpenCodeEdit,
  onCloseCodeEdit,
  onSaveCode,
  isColVis,
  catalog,
  usedTech,
  usedMoto,
}) {
  const expenseBtnRef = useRef(null);
  const tipoBtnRef = useRef(null);
  const deviceBtnRef = useRef(null);
  const pending = row.enabled === false;
  const busy = savingKey?.startsWith(`${row.idUser}:`) || enablingId === row.idUser;
  const periodConfigured = row.expensePeriodicity === 1 || row.expensePeriodicity === 2 || row.expensePeriodicity === 3;
  const expenseTooltip = periodConfigured ? 'Alterar período' : 'Configurar periodicidade';
  const devices = Array.isArray(row.devices) ? row.devices : [];
  const hasDevices = devices.length > 0;

  return (
    <tr className={`go-usr-row${hidden ? ' is-hidden' : ''}${pending ? ' is-pending' : ''}`}>
      <GoEnableCell
        row={row}
        busy={enablingId === row.idUser}
        disabling={disablingId === row.idUser}
        onEnable={onEnable}
        onRequestDisable={onRequestDisable}
      />
      <td className="go-usr-td go-usr-td-name">
        <div className="go-usr-name-cell">
          <GoAvatar photoUrl={photoUrl} initials={row.initials} />
          <div className="go-usr-name-with-code">
            <span className="go-usr-name">{row.username || '—'}</span>
            <GoUsrCodePills
              row={row}
              disabled={busy}
              codeEdit={codeEdit}
              onOpenCodeEdit={onOpenCodeEdit}
              onCloseCodeEdit={onCloseCodeEdit}
              onSaveCode={onSaveCode}
              catalog={catalog}
              usedTech={usedTech}
              usedMoto={usedMoto}
            />
          </div>
        </div>
      </td>
      {isColVis('tipo_user') ? (
        <td className="go-usr-td go-usr-td-tipo">
          {pending ? null : (
            <>
              <GoHoverTooltip text="Clique para alterar" align="start">
                <button
                  ref={tipoBtnRef}
                  type="button"
                  className="go-usr-tipo-tag"
                  disabled={busy}
                  aria-label={`Tipo: ${tipoUserLabel(row.tipoUser)}. Clique para alterar`}
                  onClick={() => {
                    setExpenseOpenId(null);
                    setDeviceOpenId(null);
                    setTipoOpenId((prev) => (prev === row.idUser ? null : row.idUser));
                    onCloseCodeEdit();
                  }}
                >
                  <span className="go-usr-tipo-tag-label">{tipoUserLabel(row.tipoUser)}</span>
                </button>
              </GoHoverTooltip>
              <TipoUserPopover
                open={tipoOpenId === row.idUser}
                anchorRef={tipoBtnRef}
                value={row.tipoUser}
                onClose={() => setTipoOpenId(null)}
                onSelect={(tipo) => {
                  onChangeTipoUser(row, tipo);
                  setTipoOpenId(null);
                }}
              />
            </>
          )}
        </td>
      ) : null}
      {isColVis('use_public_transportation') ? (
        <td className="go-usr-td go-usr-td-center">
          {pending ? null : (
            <GoToggle
              checked={row.usePublicTransportation === 1}
              disabled={busy}
              ariaLabel="Transporte público"
              tooltip="Transporte público"
              onChange={(next) => onPatch(row.idUser, { use_public_transportation: next ? 1 : 0 })}
            />
          )}
        </td>
      ) : null}
      {isColVis('asset_costs') ? (
        <td className="go-usr-td go-usr-td-center">
          {pending ? null : (
            <div className="go-usr-asset-wrap">
              <GoToggle
                checked={row.assetCosts === 1}
                disabled={busy}
                ariaLabel="Despesas"
                onChange={(next) => onPatch(row.idUser, { asset_costs: next ? 1 : 0 })}
              />
              {row.assetCosts === 1 ? (
                <>
                  <GoHoverTooltip text={expenseTooltip} align="end">
                    <button
                      ref={expenseBtnRef}
                      type="button"
                      className="go-usr-expense-icon-btn"
                      aria-label={expenseTooltip}
                      disabled={busy}
                      onClick={() => {
                        setTipoOpenId(null);
                        setDeviceOpenId(null);
                        onCloseCodeEdit();
                        setExpenseOpenId((prev) => (prev === row.idUser ? null : row.idUser));
                      }}
                    >
                      <IconCalendarCog size={16} stroke={1.75} aria-hidden />
                    </button>
                  </GoHoverTooltip>
                  <ExpensePeriodPopover
                    open={expenseOpenId === row.idUser}
                    anchorRef={expenseBtnRef}
                    value={row.expensePeriodicity}
                    onClose={() => setExpenseOpenId(null)}
                    onSelect={(period) => {
                      onPatch(row.idUser, { expense_periodicity: period });
                      setExpenseOpenId(null);
                    }}
                  />
                </>
              ) : null}
            </div>
          )}
        </td>
      ) : null}
      {isColVis('allow_manual_wo') ? (
        <td className="go-usr-td go-usr-td-center">
          {pending ? null : (
            <GoToggle
              checked={row.allowManualWo === 1}
              disabled={busy}
              ariaLabel="Abre OS avulsa"
              tooltip="Abre OS avulsa"
              tooltipAlign="end"
              onChange={(next) => onPatch(row.idUser, { allow_manual_wo: next ? 1 : 0 })}
            />
          )}
        </td>
      ) : null}
      {isColVis('devices') ? (
        <td className="go-usr-td go-usr-td-center">
          {pending ? null : (
            <>
              <GoHoverTooltip
                text={hasDevices ? 'Ver dispositivos' : 'Sem dispositivos'}
                align="end"
              >
                <button
                  ref={deviceBtnRef}
                  type="button"
                  className={`go-usr-device-btn${hasDevices ? '' : ' is-empty'}`}
                  aria-label="Ver dispositivos"
                  onClick={() => {
                    setExpenseOpenId(null);
                    setTipoOpenId(null);
                    onCloseCodeEdit();
                    setDeviceOpenId((prev) => (prev === row.idUser ? null : row.idUser));
                  }}
                >
                  <IconDeviceMobileSearch size={16} stroke={1.75} aria-hidden />
                  {hasDevices && devices.length > 1 ? (
                    <span className="go-usr-device-count">{devices.length}</span>
                  ) : null}
                </button>
              </GoHoverTooltip>
              <DevicePopover
                open={deviceOpenId === row.idUser}
                anchorRef={deviceBtnRef}
                devices={devices}
                onClose={() => setDeviceOpenId(null)}
              />
            </>
          )}
        </td>
      ) : null}
    </tr>
  );
}

function getSortValue(row, key) {
  switch (key) {
    case 'username':
      return String(row.username || '').toLowerCase();
    case 'codtec':
      return String(row.codtec || '');
    case 'tipo_user':
      return String(row.tipoUser || 1);
    case 'use_public_transportation':
      return row.usePublicTransportation === 1 ? '1' : '0';
    case 'asset_costs':
      return row.assetCosts === 1 ? '1' : '0';
    case 'allow_manual_wo':
      return row.allowManualWo === 1 ? '1' : '0';
    default:
      return '';
  }
}

function GoUsuariosTable({
  rows,
  photoMap,
  savingKey,
  enablingId,
  disablingId,
  onPatch,
  onChangeTipoUser,
  onEnable,
  onRequestDisable,
  searchQuery,
  sortBy,
  isColVis,
  catalog,
  usedTech,
  usedMoto,
}) {
  const [collapsedGroups, setCollapsedGroups] = useState({ 'nao-habilitados': true });
  const [expenseOpenId, setExpenseOpenId] = useState(null);
  const [tipoOpenId, setTipoOpenId] = useState(null);
  const [deviceOpenId, setDeviceOpenId] = useState(null);
  const [codeEdit, setCodeEdit] = useState(null);

  const onOpenCodeEdit = useCallback((idUser, field) => {
    setExpenseOpenId(null);
    setTipoOpenId(null);
    setDeviceOpenId(null);
    setCodeEdit({ idUser, field, saving: false });
  }, []);

  const onCloseCodeEdit = useCallback(() => {
    setCodeEdit(null);
  }, []);

  const onSaveCode = useCallback(
    async (idUser, field, value) => {
      setCodeEdit((prev) => (prev ? { ...prev, saving: true } : prev));
      const ok = await onPatch(idUser, { [field]: value });
      if (ok) setCodeEdit(null);
      else setCodeEdit((prev) => (prev ? { ...prev, saving: false } : prev));
    },
    [onPatch]
  );

  const filteredSortedRows = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    let list = rows;
    if (q) {
      list = list.filter((r) => {
        const hay = `${r.username || ''} ${r.codtec || ''} ${r.codmoto || ''} ${r.email || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return [...list].sort((a, b) => {
      const av = getSortValue(a, sortBy);
      const bv = getSortValue(b, sortBy);
      return String(av).localeCompare(String(bv), 'pt-BR', { numeric: true, sensitivity: 'base' });
    });
  }, [rows, searchQuery, sortBy]);

  const groupedRows = useMemo(() => {
    const map = {};
    GO_GROUPS.forEach((g) => {
      map[g.key] = filteredSortedRows.filter((r) => categorizeGoUser(r) === g.key);
    });
    return map;
  }, [filteredSortedRows]);

  const groupsWithRows = useMemo(
    () => GO_GROUPS.filter((g) => (groupedRows[g.key]?.length ?? 0) > 0),
    [groupedRows]
  );

  const allCollapsed = useMemo(
    () => groupsWithRows.length > 0 && groupsWithRows.every((g) => !!collapsedGroups[g.key]),
    [groupsWithRows, collapsedGroups]
  );

  const visibleColCount = useMemo(() => {
    let n = 2;
    GO_COL_KEYS.forEach((k) => {
      if (isColVis(k)) n += 1;
    });
    return n;
  }, [isColVis]);

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleAllGroups = useCallback(() => {
    const nextAllCollapsed = !allCollapsed;
    const nextCollapsed = {};
    groupsWithRows.forEach((g) => {
      nextCollapsed[g.key] = nextAllCollapsed;
    });
    setCollapsedGroups(nextCollapsed);
  }, [allCollapsed, groupsWithRows]);

  if (!rows.length) {
    return (
      <div className="go-usr-root">
        <div className="go-usr-panel">
          <div className="go-usr-foot-note">Nenhum usuário encontrado para este cliente.</div>
        </div>
      </div>
    );
  }

  if (!filteredSortedRows.length) {
    return (
      <div className="go-usr-root">
        <div className="go-usr-panel">
          <div className="go-usr-foot-note">Nenhum resultado para a pesquisa.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="go-usr-root">
      <div className="go-usr-panel">
        <div className="go-usr-table-scroll">
          <table className="go-usr-table">
            <thead>
              <tr>
                <th className="go-usr-th-habilita">Habilita</th>
                <th>Nome</th>
                {isColVis('tipo_user') ? <th className="go-usr-th-tipo">Tipo</th> : null}
                {isColVis('use_public_transportation') ? (
                  <th className="go-usr-th-transporte">
                    <span className="go-usr-th-inner">
                      Transporte público
                      <GoThHelp text={TOOLTIP_TRANSPORTE} />
                    </span>
                  </th>
                ) : null}
                {isColVis('asset_costs') ? (
                  <th className="go-usr-th-despesas">
                    <span className="go-usr-th-inner">
                      Despesas
                      <GoThHelp text={TOOLTIP_DESPESAS} />
                    </span>
                  </th>
                ) : null}
                {isColVis('allow_manual_wo') ? (
                  <th className="go-usr-th-avulsa">Abre OS avulsa</th>
                ) : null}
                {isColVis('devices') ? (
                  <th className="go-usr-th-dispositivo">Dispositivo</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {GO_GROUPS.map((group) => {
                const groupRows = groupedRows[group.key] || [];
                if (!groupRows.length) return null;
                const collapsed = !!collapsedGroups[group.key];
                const dotStyle =
                  group.key === 'nao-habilitados'
                    ? { border: `1.5px dashed ${group.color}`, background: 'transparent' }
                    : { background: group.color };

                return (
                  <React.Fragment key={group.key}>
                    <tr
                      className={`go-usr-group-row${collapsed ? ' collapsed' : ''}`}
                      onClick={() => toggleGroup(group.key)}
                    >
                      <td colSpan={visibleColCount}>
                        <div className="go-usr-group-label">
                          <span className="go-usr-chev">
                            <ChevronDown size={13} strokeWidth={2.5} />
                          </span>
                          <span className="go-usr-dot" style={dotStyle} />
                          {group.label}
                          <span className="go-usr-group-count">· {groupRows.length}</span>
                          <span className="go-usr-group-desc">{group.desc}</span>
                        </div>
                      </td>
                    </tr>
                    {groupRows.map((row) => (
                      <GoUserRow
                        key={row.idUser}
                        row={row}
                        photoUrl={photoMap?.[String(row.idUser)] || null}
                        hidden={collapsed}
                        savingKey={savingKey}
                        enablingId={enablingId}
                        disablingId={disablingId}
                        onPatch={onPatch}
                        onChangeTipoUser={onChangeTipoUser}
                        onEnable={onEnable}
                        onRequestDisable={onRequestDisable}
                        expenseOpenId={expenseOpenId}
                        setExpenseOpenId={setExpenseOpenId}
                        tipoOpenId={tipoOpenId}
                        setTipoOpenId={setTipoOpenId}
                        deviceOpenId={deviceOpenId}
                        setDeviceOpenId={setDeviceOpenId}
                        codeEdit={codeEdit}
                        onOpenCodeEdit={onOpenCodeEdit}
                        onCloseCodeEdit={onCloseCodeEdit}
                        onSaveCode={onSaveCode}
                        isColVis={isColVis}
                        catalog={catalog}
                        usedTech={usedTech}
                        usedMoto={usedMoto}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="go-usr-foot-note">
          <span>
            {filteredSortedRows.length} usuário{filteredSortedRows.length !== 1 ? 's' : ''} ·{' '}
            {groupsWithRows.length} grupos
          </span>
          <button type="button" className="go-usr-foot-toggle" onClick={toggleAllGroups}>
            {allCollapsed ? 'Expandir grupos' : 'Recolher grupos'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GoUsuariosConfig() {
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [enablingId, setEnablingId] = useState(null);
  const [enablePrompt, setEnablePrompt] = useState(null);
  const [enableTipoUser, setEnableTipoUser] = useState(1);
  const [enableCodtech, setEnableCodtech] = useState('');
  const [enableCodmoto, setEnableCodmoto] = useState('');
  const [tipoCodesPrompt, setTipoCodesPrompt] = useState(null);
  const [disablingId, setDisablingId] = useState(null);
  const [disablePrompt, setDisablePrompt] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('username');
  const [colVisibility, setColVisibility] = useState(fullColVisibility);
  const [linkCatalog, setLinkCatalog] = useState({ tech: [], moto: [] });

  const isColVis = useCallback((key) => colVisibility[key] !== false, [colVisibility]);
  const { usedTech, usedMoto } = useMemo(() => buildGoCodeUsage(rows), [rows]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/go/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Falha ao carregar usuários Go');
      }
      setRows(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      setError(e.message || 'Erro ao carregar');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('authToken');
    fetch(`${API_BASE_URL}/api/config/go/users/link-catalog`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.success) return;
        const payload = data.data;
        if (payload && !Array.isArray(payload) && (payload.tech || payload.moto)) {
          setLinkCatalog({
            tech: Array.isArray(payload.tech) ? payload.tech : [],
            moto: Array.isArray(payload.moto) ? payload.moto : [],
          });
          return;
        }
        setLinkCatalog({
          tech: Array.isArray(payload) ? payload : [],
          moto: [],
        });
      })
      .catch(() => {
        if (!cancelled) setLinkCatalog({ tech: [], moto: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const photoSubjects = useMemo(
    () =>
      rows.map((r) => ({
        id: r.idUser,
        hasProfilePhoto: r.hasProfilePhoto === true,
        profilePhotoUrl: r.profilePhotoUrl || null,
      })),
    [rows]
  );
  const photoMap = useUserProfilePhotos(photoSubjects);

  const onPatch = useCallback(
    async (idUser, patch) => {
      const conflict = goCodeConflictMessage(rows, idUser, patch);
      if (conflict) {
        toastError(conflict);
        return false;
      }

      const key = `${idUser}:${Object.keys(patch).join(',')}`;
      setSavingKey(key);

      const prevRows = rows;
      setRows((list) =>
        list.map((r) => {
          if (r.idUser !== idUser) return r;
          const next = { ...r };
          if (Object.prototype.hasOwnProperty.call(patch, 'tipo_user')) {
            next.tipoUser = patch.tipo_user;
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'use_public_transportation')) {
            next.usePublicTransportation = patch.use_public_transportation;
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'asset_costs')) {
            next.assetCosts = patch.asset_costs;
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'expense_periodicity')) {
            next.expensePeriodicity = patch.expense_periodicity;
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'allow_manual_wo')) {
            next.allowManualWo = patch.allow_manual_wo;
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'codtech')) {
            next.codtec = patch.codtech ? String(patch.codtech).trim() : '';
          }
          if (Object.prototype.hasOwnProperty.call(patch, 'codmoto')) {
            next.codmoto = patch.codmoto ? String(patch.codmoto).trim() : '';
          }
          return next;
        })
      );

      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/config/go/users/${idUser}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(patch),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Falha ao salvar');
        }
        toastSuccess('Configuração salva');
        return true;
      } catch (e) {
        setRows(prevRows);
        toastError(e.message || 'Erro ao salvar');
        return false;
      } finally {
        setSavingKey(null);
      }
    },
    [rows, toastSuccess, toastError]
  );

  const submitEnable = useCallback(
    async (idUser, tipoUser, codes = {}) => {
      setEnablingId(idUser);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/config/go/users/${idUser}/enable`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tipo_user: tipoUser,
            codtech: codes.codtech ?? '',
            codmoto: codes.codmoto ?? '',
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Falha ao habilitar');
        }
        setEnablePrompt(null);
        setEnableTipoUser(1);
        setEnableCodtech('');
        setEnableCodmoto('');
        toastSuccess('Usuário habilitado no App Go');
        await loadRows();
      } catch (e) {
        toastError(e.message || 'Erro ao habilitar');
      } finally {
        setEnablingId(null);
      }
    },
    [loadRows, toastSuccess, toastError]
  );

  const onEnable = useCallback(
    (row) => {
      if (!row?.idUser) return;
      setEnableTipoUser(1);
      const tech = sanitizeGoCode(row.codtec || '');
      const moto = sanitizeGoCode(row.codmoto || '');
      setEnableCodtech(isGoCodeTaken(usedTech, tech, row.idUser) ? '' : tech);
      setEnableCodmoto(isGoCodeTaken(usedMoto, moto, row.idUser) ? '' : moto);
      setEnablePrompt({ idUser: row.idUser, username: row.username || row.email || '' });
    },
    [usedTech, usedMoto]
  );

  const onConfirmEnable = useCallback(async () => {
    if (!enablePrompt?.idUser || !enableTipoUser) return;
    const built = buildCodesPatchForTipo(enableTipoUser, {
      codtechInput: enableCodtech,
      codmotoInput: enableCodmoto,
    });
    if (!built.ok) {
      toastError(built.message);
      return;
    }
    const conflict = goCodeConflictMessage(rows, enablePrompt.idUser, built.patch);
    if (conflict) {
      toastError(conflict);
      return;
    }
    await submitEnable(enablePrompt.idUser, enableTipoUser, built.patch);
  }, [
    enablePrompt,
    enableTipoUser,
    enableCodtech,
    enableCodmoto,
    submitEnable,
    toastError,
    rows,
  ]);

  const onChangeTipoUser = useCallback(
    async (row, tipo) => {
      if (!row?.idUser || Number(row.tipoUser) === Number(tipo)) return;
      const built = buildCodesPatchForTipo(tipo, {
        codtec: row.codtec,
        codmoto: row.codmoto,
      });
      if (!built.ok) {
        setTipoCodesPrompt({
          idUser: row.idUser,
          username: row.username || row.email || '',
          tipo,
          codtech: built.nextTech || '',
          codmoto: built.nextMoto || '',
        });
        return;
      }
      await onPatch(row.idUser, {
        tipo_user: tipo,
        ...built.patch,
      });
    },
    [onPatch]
  );

  const onConfirmTipoCodes = useCallback(async () => {
    if (!tipoCodesPrompt?.idUser || !tipoCodesPrompt?.tipo) return;
    const built = buildCodesPatchForTipo(tipoCodesPrompt.tipo, {
      codtechInput: tipoCodesPrompt.codtech,
      codmotoInput: tipoCodesPrompt.codmoto,
    });
    if (!built.ok) {
      toastError(built.message);
      return;
    }
    const ok = await onPatch(tipoCodesPrompt.idUser, {
      tipo_user: tipoCodesPrompt.tipo,
      ...built.patch,
    });
    if (ok) setTipoCodesPrompt(null);
  }, [tipoCodesPrompt, onPatch, toastError]);

  const onEnableTipoChange = useCallback((tipo) => {
    setEnableTipoUser(tipo);
    const policy = codesPolicyForTipo(tipo);
    if (!policy.allowCodtech) setEnableCodtech('');
    if (!policy.allowCodmoto) setEnableCodmoto('');
  }, []);

  const resetEnableForm = useCallback(() => {
    setEnablePrompt(null);
    setEnableTipoUser(1);
    setEnableCodtech('');
    setEnableCodmoto('');
  }, []);

  const onRequestDisable = useCallback((row) => {
    if (!row?.idUser) return;
    setDisablePrompt({ idUser: row.idUser, username: row.username || row.email || '' });
  }, []);

  const onDisable = useCallback(
    async (idUser) => {
      setDisablingId(idUser);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/config/go/users/${idUser}/disable`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Falha ao desabilitar');
        }
        setDisablePrompt(null);
        toastSuccess('Usuário desabilitado no App Go');
        await loadRows();
      } catch (e) {
        toastError(e.message || 'Erro ao desabilitar');
      } finally {
        setDisablingId(null);
      }
    },
    [loadRows, toastSuccess, toastError]
  );

  const onConfirmDisable = useCallback(async () => {
    if (!disablePrompt?.idUser) return;
    await onDisable(disablePrompt.idUser);
  }, [disablePrompt, onDisable]);

  return (
    <div className="pcs2-page go-usuarios-page">
      {enablePrompt ? (
        <GoEnableTipoModal
          tipoUser={enableTipoUser}
          onTipoChange={onEnableTipoChange}
          enableCodtech={enableCodtech}
          onEnableCodtechChange={setEnableCodtech}
          enableCodmoto={enableCodmoto}
          onEnableCodmotoChange={setEnableCodmoto}
          onCancel={resetEnableForm}
          onConfirm={onConfirmEnable}
          submitting={enablingId === enablePrompt.idUser}
          catalog={linkCatalog}
          usedTech={usedTech}
          usedMoto={usedMoto}
          excludeIdUser={enablePrompt.idUser}
        />
      ) : null}
      {tipoCodesPrompt ? (
        <GoTipoChangeCodesModal
          username={tipoCodesPrompt.username}
          tipoUser={tipoCodesPrompt.tipo}
          codtech={tipoCodesPrompt.codtech}
          onCodtechChange={(v) => setTipoCodesPrompt((prev) => (prev ? { ...prev, codtech: v } : prev))}
          codmoto={tipoCodesPrompt.codmoto}
          onCodmotoChange={(v) => setTipoCodesPrompt((prev) => (prev ? { ...prev, codmoto: v } : prev))}
          onCancel={() => setTipoCodesPrompt(null)}
          onConfirm={onConfirmTipoCodes}
          submitting={savingKey?.startsWith(`${tipoCodesPrompt.idUser}:`)}
          catalog={linkCatalog}
          usedTech={usedTech}
          usedMoto={usedMoto}
          excludeIdUser={tipoCodesPrompt.idUser}
        />
      ) : null}
      {disablePrompt ? (
        <GoDisableConfirmModal
          username={disablePrompt.username}
          onCancel={() => setDisablePrompt(null)}
          onConfirm={onConfirmDisable}
          submitting={disablingId === disablePrompt.idUser}
        />
      ) : null}
      <Pcs2Section
        className="go-usuarios-section-table"
        icon={Users}
        title="Gestão de usuários"
        desc="Gerencie técnicos e entregadores no App Go"
      >
        <Pcs2Subblock
          title="Gestão de técnicos e entregadores"
          desc="Vincule usuários a função de técnico ou entregador no App Go."
          action={
            <GoUsuariosToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              isColVis={isColVis}
              onToggleCol={(k) => {
                setColVisibility((prev) => ({ ...prev, [k]: prev[k] === false ? true : false }));
              }}
            />
          }
        >
          {loading ? (
            <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando usuários" />
          ) : error ? (
            <div className="go-usr-error">
              <span>{error}</span>
              <button type="button" className="go-usr-retry" onClick={loadRows}>
                Tentar novamente
              </button>
            </div>
          ) : (
            <GoUsuariosTable
              rows={rows}
              photoMap={photoMap}
              savingKey={savingKey}
              enablingId={enablingId}
              disablingId={disablingId}
              onPatch={onPatch}
              onChangeTipoUser={onChangeTipoUser}
              onEnable={onEnable}
              onRequestDisable={onRequestDisable}
              searchQuery={searchQuery}
              sortBy={sortBy}
              isColVis={isColVis}
              catalog={linkCatalog}
              usedTech={usedTech}
              usedMoto={usedMoto}
            />
          )}
        </Pcs2Subblock>
      </Pcs2Section>
      {Toaster}
    </div>
  );
}
